import React, { Component } from 'react';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AnnouncementBanner from '../components/AnnouncementBanner';
import Breadcrumb from '../components/Breadcrumb';

import { buildPath } from '../utils/route_utils';
import { updateProductSEO } from '../utils/seo_utils';
import { trackEvent } from '../utils/analytics';
import { observeRise } from '../utils/reveal';

import '../css/OdoPage.css';

/**
 * OdoPage: the odo series product page (Autonomous Inspection Quadruped),
 * served at /:lang/tech/odo (the old /tech/collie path redirects here).
 *
 * Full bleed and image-led: every section spans the viewport, copy sits
 * inside padding-inline: var(--bleed), grids are edge-to-edge tiles with
 * 1 px --line gaps or hairline-separated columns, and media runs edge to
 * edge. It uses the shared dark-site primitives from App.css (.bleed,
 * .h-display, .p-lede, .sec-title, .sec-lede, .btn-*, .rise) plus
 * OdoPage.css. All copy lives in COPY below with { zh, en, ja } keys; t()
 * picks the current language and falls back to en. Headlines stand alone:
 * no eyebrow labels above them.
 *
 * Order: hero, what it does, capabilities (patrol footage, fleet console,
 * checkpoint record), terrain, platform, what a site needs, the family, CTA.
 * Copy never names a site, a customer or a figure from a deployment; media
 * is never overlaid with text.
 */

const LANGS = ['zh', 'en', 'ja'];
const CONTACT_ROUTE = '/contact';
const PRODUCTS_ROUTE = '/solutions';

const ASSETS = {
  odo9: { webp: '/assets/odo/odo9.webp', png: '/assets/odo/odo9.png', w: 1311, h: 1297 },
  odo3: { webp: '/assets/odo/odo3.webp', png: '/assets/odo/odo3.png', w: 1400, h: 933 },
  odo1: { webp: '/assets/odo/odo1.webp', png: '/assets/odo/odo1.png', w: 1149, h: 1030 },
  logo9: '/assets/odo/logo/nexuni_odo_9_horizontal_white.svg',
  logo3: '/assets/odo/logo/nexuni_odo_3_horizontal_white.svg',
  logo1: '/assets/odo/logo/nexuni_odo_1_horizontal_white.svg',
  fieldVideo: '/video/odo_sg_field',
  fieldPoster: '/assets/odo/deploy/odo_sg_field_poster.jpg',
  consoleVideo: '/video/odo_sg_console',
  consolePoster: '/assets/odo/deploy/odo_sg_console_poster.jpg',
  terrain: { webp: '/assets/odo/hero/odo1_steps.webp', jpg: '/assets/odo/hero/odo1_steps.jpg', w: 1590, h: 614 },
  heroSub: { webp: '/assets/odo/hero/odo9_substation.webp', jpg: '/assets/odo/hero/odo9_substation.jpg', m: '/assets/odo/hero/odo9_substation_m.webp', w: 2200, h: 1395 },
  simVideo: '/assets/odo/sim/construction_patrol',
  simPoster: '/assets/odo/sim/construction_patrol_poster.jpg',
  logoOdo: '/assets/odo/logo/nexuni_odo_horizontal_white.svg'
};

// Inline icons: 24-box, 2 px white stroke, decorative.
const svgAttrs = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false'
};

const ICON = {
  route: (
    <svg {...svgAttrs}>
      <circle cx="5" cy="18" r="2.5" />
      <circle cx="19" cy="6" r="2.5" />
      <path d="M7.5 18H12a4 4 0 0 0 0-8h-1a3 3 0 0 1 0-6h5.5" />
    </svg>
  ),
  bell: (
    <svg {...svgAttrs}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  ),
  gauge: (
    <svg {...svgAttrs}>
      <path d="m12 14 4-4" />
      <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    </svg>
  )
};

const COPY = {
  name: { zh: '自主巡檢機器狗', en: 'Autonomous Inspection Quadruped', ja: '自律巡回四足ロボット' },
  crumbProducts: { zh: '產品', en: 'Products', ja: '製品' },

  hero: {
    seriesLine: { zh: '系列', en: 'series', ja: 'シリーズ' },
    desc: {
      zh: '三款型號共用同一顆 NIRO 大腦，負責巡邏、巡檢與現場紀錄；旗艦款 odo 9 採用完全由 Nexuni 自製的四足平台。',
      en: 'Three models share one NIRO brain for patrol, inspection and on-site records. The flagship odo 9 runs on a fully Nexuni-made quadruped platform.',
      ja: '3 つのモデルがひとつの NIRO の頭脳を共有し、巡回・点検・現場記録を担います。フラッグシップの odo 9 は、Nexuni が自社で設計・製造する四足プラットフォームです。'
    },
  },

  glance: {
    titleCus: { zh: '現有業界問題/挑戰', en: 'Current Industry Challenges', ja: '業界の課題' },
    cus: {
      zh: [
        '廠區大、班次長，巡邏與巡檢的人力難找、流動率高，夜班和假日更難排。',
        '儀表、閥件與設備溫度靠人工定時抄錄，容易漏檢、誤讀，異常多在事後才發現。',
        '高壓、高溫、氣體外洩或密閉空間等區域，人員進入本身就有風險。',
        '固定攝影機與感測器有死角，也無法主動走到現場確認異常。',
        '巡檢紀錄多是紙本或口頭回報，缺少時間戳與影像證據，難以追溯稽核。'
      ],
      en: [
        'Large sites and long shifts make patrol and inspection staff hard to hire and keep, with nights and holidays the hardest to cover.',
        'Gauges, valves and equipment temperatures are logged by hand on a schedule, so readings get missed or misread and faults surface late.',
        'High-voltage, high-temperature, gas-leak and confined areas put people at risk just by entering.',
        'Fixed cameras and sensors leave blind spots and cannot walk over to check an anomaly.',
        'Records are mostly paper or verbal, without timestamps or images, so rounds are hard to trace and audit.'
      ],
      ja: [
        '広い敷地と長いシフトで、巡回・点検の人員は採用も定着も難しく、夜間や休日はさらに手が足りない。',
        '計器・バルブ・設備温度は人が定時に手書きで記録するため、見落としや読み間違いが起き、異常の発見が遅れがち。',
        '高電圧・高温・ガス漏れ・閉所などのエリアは、立ち入るだけで人に危険が及ぶ。',
        '固定カメラやセンサーには死角があり、異常箇所まで自ら確認しに行くこともできない。',
        '記録は紙や口頭が中心で、タイムスタンプや画像がなく、追跡や監査が難しい。'
      ]
    },
    titlePs: { zh: '我們的產品', en: 'Our Product', ja: '私たちの製品' },
    ps: {
      zh: [
        'Nexuni 自主巡檢機器狗（odo 系列）依排程或事件自主巡邏整個廠區，讀取儀表、拍攝熱像、偵測氣體與異音，樓梯、斜坡與戶外都能走。',
        '每個檢查點都留下有時間戳的影像與數據，異常即時推播到管理平台，並可派機器人前往確認。人不必進入高風險區域，巡檢也不再漏站。'
      ],
      en: [
        'The Nexuni Autonomous Inspection Quadruped (odo series) patrols the whole plant on a schedule or on an event, reading gauges, capturing thermal images and detecting gas and abnormal sounds, on stairs, slopes and outdoor ground alike.',
        'Every checkpoint leaves a timestamped image and reading. Anomalies are pushed to the management platform in real time and a robot can be sent to confirm them, so nobody has to enter a high-risk area and no station gets skipped.'
      ],
      ja: [
        'Nexuni 自律巡回四足ロボット（odo シリーズ）は、スケジュールやイベントに応じて工場全体を自律巡回し、計器の読み取り、熱画像の撮影、ガスや異音の検知を行います。階段・斜面・屋外も走行できます。',
        '各チェックポイントにタイムスタンプ付きの画像とデータが残り、異常はリアルタイムで管理プラットフォームへ通知。確認のためにロボットを向かわせることもでき、人が高リスク区域に立ち入る必要も、点検の抜けもなくなります。'
      ]
    }
  },

  brand: {
    title: { zh: '為什麼叫 odo', en: 'Why the name odo', ja: 'なぜ odo という名前か' },
    text: {
      zh: 'odo 取自機器人學的 odometry：知道自己在哪裡、要往哪裡去。odo 也是 all + do：不只看、不只分析，而是進入現場，把任務做完。',
      en: 'odo comes from odometry, the robotics term for knowing where you are and where you are going. It is also all + do: not just seeing or analysing, but going in and getting the job done.',
      ja: 'odo はロボティクスの用語 odometry に由来します。自分の位置と行き先を知ること。そして all + do でもあります。見るだけ、分析するだけでなく、現場に入って仕事をやり遂げることです。'
    }
  },

  what: {
    label: { zh: '機器狗的三種工作', en: 'What odo does', ja: 'odo の 3 つの仕事' },
    cards: [
      {
        icon: 'route',
        title: { zh: '排程巡邏', en: 'Scheduled rounds', ja: 'スケジュール巡回' },
        text: {
          zh: '依固定路線與檢查點巡邏，日夜運作，沒電自動回充。',
          en: 'Fixed routes and checkpoints, day and night, with auto-recharge.',
          ja: '固定ルートとチェックポイントを昼夜巡回。充電は自動。'
        }
      },
      {
        icon: 'bell',
        title: { zh: '事件即時反應', en: 'Event-driven response', ja: 'イベント対応' },
        text: {
          zh: '有人闖入、警報響起或 CCTV 偵測到異常，機器狗就會前往現場查看並回報。',
          en: 'When an intruder is spotted, an alarm goes off or the CCTV raises an alert, the robot heads to the spot, checks it and reports back.',
          ja: '侵入者、警報、CCTV の異常検知があれば、odo が現場に向かい、確認して報告します。'
        }
      },
      {
        icon: 'gauge',
        title: { zh: '巡檢與讀值', en: 'Inspection & readings', ja: '点検と読み取り' },
        text: {
          zh: '讀取儀表、熱像、洩漏與氣體數值，在機器人上直接判讀，每個檢查點都附上照片。',
          en: 'Gauges, thermal, leaks and gas, judged on board with an evidence photo per stop.',
          ja: '計器・熱画像・漏れ・ガスをロボット上で判定。各ポイントに証拠写真を 1 枚添付。'
        }
      }
    ]
  },

  capabilities: {
    title: {
      zh: '完全自動化的巡邏巡檢',
      en: 'Fully automated patrol and inspection.',
      ja: '巡回・点検を完全自動化。'
    },
    sub: {
      zh: '依排程執行例行巡邏；偵測到異常或接獲指令時，立即切換任務前往現場查看，縮短處置時間。',
      en: 'Routine rounds run on a schedule. When an anomaly is detected or a command comes in, the robot switches tasks immediately and heads to the area to check the scene, cutting incident response time.',
      ja: 'スケジュールに沿って定期巡回。異常検知や指示があれば即座にタスクを切り替えて現場へ向かい、対応時間を短縮します。'
    },

    // Row A: the autonomous round (field footage left, copy right).
    patrol: {
      title: {
        zh: '可白天晚上全時段 24/7 巡邏',
        en: 'Patrols 24/7, day and night.',
        ja: '昼も夜も、24 時間 365 日巡回。'
      },
      sub: {
        zh: 'odo 系列機器狗依照排程路線自己巡邏，遇到行人會讓路，天黑也照常完成，不需要有人操控。',
        en: 'odo series robots follow the scheduled route, give way to pedestrians and finish the round in the dark, with no one driving.',
        ja: 'odo シリーズのロボットはスケジュールされたルートを自ら巡回し、歩行者に道を譲り、暗くなっても誰も操縦することなく巡回を終えます。'
      },
      bullets: {
        zh: [
          '依排程或臨時指派的路線與檢查點巡邏，巡完自己回充電座',
          '遇到行人或障礙物會減速、讓路並重新規劃路徑'
        ],
        en: [
          'Scheduled or on-demand routes with checkpoints; back to the dock to recharge between rounds',
          'Slows, gives way and re-plans around pedestrians and obstacles'
        ],
        ja: [
          'スケジュール巡回にも臨時の指示にも対応。チェックポイントを回り、終われば自らドックに戻って充電',
          '歩行者や障害物に対して減速し、道を譲り、経路を再計画'
        ]
      },
      alt: {
        zh: 'odo 1 夜間在市區廣場自主巡邏',
        en: 'odo 1 on an autonomous night round through an urban plaza',
        ja: '都市広場を夜間に自律巡回する odo 1'
      }
    },

    // Row B: the fleet console (head, full-width footage, three cells, foot).
    console: {
      title: {
        zh: '一個畫面，掌握整個機隊。',
        en: 'Supervise the whole fleet from one screen.',
        ja: 'ひとつの画面で、フリート全体を監視。'
      },
      sub: {
        zh: 'NIRO 機隊控制台把每台機器人的即時影像、位置、任務紀錄與事件集中在同一個畫面，一位操作員就能掌握，需要時也能隨時手動接管。',
        en: "NIRO's fleet console puts every robot's live cameras, map position, task history and event log in front of one operator, who can take manual control at any moment.",
        ja: 'NIRO のフリートコンソールは、各ロボットのライブ映像・地図上の位置・タスク履歴・イベントログをひとりのオペレーターの前に集約し、いつでも手動操作に切り替えられます。'
      },
      alt: {
        zh: 'NIRO 機隊控制台畫面',
        en: 'NIRO fleet console screen',
        ja: 'NIRO フリートコンソールの画面'
      },
      features: [
        {
          title: { zh: '事件日誌', en: 'Event log', ja: 'イベントログ' },
          text: {
            zh: '每一筆偵測都記錄時間、類型與機器人編號，並可將告警轉送到 VMS、BMS 與門禁系統。',
            en: 'Every detection with time, type and robot; alerts forwarded to your VMS, BMS and access control.',
            ja: '検知ごとに時刻・種類・ロボットを記録。アラートは VMS・BMS・入退室管理へ転送。'
          }
        },
        {
          title: { zh: '任務紀錄', en: 'Task history', ja: 'タスク履歴' },
          text: {
            zh: '每一項任務的執行結果，以及每個檢查點的證據照片。',
            en: 'Every mission with its outcome and the evidence photo from each checkpoint.',
            ja: '各ミッションの結果と、チェックポイントごとの証拠写真。'
          }
        },
        {
          title: { zh: '即時地圖', en: 'Live map', ja: 'ライブマップ' },
          text: {
            zh: '在 SLAM 地圖上即時顯示每台機器人的位置與路線，一位操作員就能監看整個機隊。',
            en: "SLAM map with each robot's position and route; one operator supervises the whole fleet.",
            ja: 'SLAM マップ上に各ロボットの位置とルート。オペレーター 1 人でフリート全体を監視。'
          }
        }
      ],
    },

    // Row C: the checkpoint record (copy left, field schema right).
    evidence: {
      title: {
        zh: '每個檢查點，都留下證據。',
        en: 'Every checkpoint leaves evidence.',
        ja: 'すべてのチェックポイントに、証拠が残る。'
      },
      sub: {
        zh: 'odo 系列機器狗在每個檢查點拍下附時間戳記的照片，在機器人上判讀並記錄結果與信心度。巡邏留下的是完整紀錄，不是口頭回報。',
        en: 'At each stop an odo series robot takes a time-stamped photo, judges it on board and files a verdict with a confidence score, so a round is a record, not a verbal report.',
        ja: '各ポイントで odo シリーズのロボットはタイムスタンプ付きの写真を撮り、ロボット上で判定し、確信度とともに結果を記録します。巡回は「報告」ではなく「記録」になります。'
      },
      bullets: {
        zh: [
          '照片附時間戳記：每一趟都在檢查點以設定好的角度拍攝',
          '判定附信心度：正常或異常直接在機器人上判讀，並附上判斷依據',
          '可匯出、可稽核：報告可經 API 或檔案匯出，完整稽核紀錄留在場域內'
        ],
        en: [
          'Time-stamped photo: taken at the checkpoint, from the angle the route defines, on every round',
          'Verdict and confidence: normal or abnormal, judged on the robot, with a confidence score and the reason',
          'Exportable and auditable: reports go to your systems through the API or as files; the full audit log stays on site'
        ],
        ja: [
          'タイムスタンプ付き写真：チェックポイントで、ルートに定めた角度から、毎回撮影',
          '判定と確信度：正常か異常かをロボット上で判定し、確信度と根拠を添付',
          'エクスポートと監査：レポートは API またはファイルで自社システムへ。完全な監査ログは現場内に保管'
        ]
      },
      // The record panel shows the FIELDS of a checkpoint record, never sample values.
      record: {
        label: { zh: '檢查點紀錄 · 每個檢查點一筆', en: 'CHECKPOINT RECORD · ONE PER STOP', ja: 'チェックポイント記録 · 1 ポイントにつき 1 件' },
        // k: the field name as the console shows it (mono); v: what the field holds.
        fields: [
          { k: 'STOP', v: { zh: '路線中的檢查點編號與名稱', en: 'checkpoint number and label from your route', ja: 'あなたのルートのチェックポイント番号と名称' } },
          { k: 'TIME', v: { zh: '機器人時鐘記錄的停留起訖時間', en: 'on-board clock, start and end of dwell', ja: 'ロボット上の時計：滞在の開始と終了' } },
          { k: 'PHOTO', v: { zh: '每個檢查點一張，PTZ 或熱像', en: 'one frame per stop, PTZ or thermal', ja: '1 ポイントにつき 1 枚。PTZ またはサーマル' } },
          { k: 'VERDICT', chips: ['PASS', 'ATTENTION', 'FAIL'], v: { zh: '附信心度', en: 'with confidence', ja: '確信度つき' } },
          { k: 'ACTION', v: { zh: 'Webhook 通知、開立工單或人工複核', en: 'webhook, ticket, or human review', ja: 'Webhook・チケット・人による確認' } }
        ]
      }
    }
  },

  terrain: {
    title: {
      zh: '跨樓層全覆蓋，樓梯/陡坡/電梯都能走',
      en: 'Full multi-floor coverage: stairs, steep slopes and elevators.',
      ja: 'フロアを跨ぐ全域カバー：階段・急斜面・エレベーターに対応'
    },
    sub: {
      zh: '具備高機動性設計，可上下樓梯、行走陡坡與不平整地面，並能搭配電梯動線完成多樓層巡邏，真正把巡檢範圍從「單點監控」升級為「全域巡防」。',
      en: 'A high-mobility design takes stairs, steep slopes and uneven ground, and works with the elevator system to patrol across floors, turning inspection from single-point monitoring into full-area coverage.',
      ja: '高い機動性を備えた設計により、階段の昇降、急斜面や不整地の走行が可能です。エレベーターと連携した動線で複数階の巡回を完了させ、点検範囲を従来の「単一ポイント監視」から「全域巡回警備」へとアップグレードします。'
    },
    alt: {
      zh: 'nexuni odo 1 在戶外花崗岩階梯上行走',
      en: 'nexuni odo 1 climbing outdoor granite steps',
      ja: '屋外の御影石の階段を上る nexuni odo 1'
    },
    cards: [
      {
        title: { zh: '樓梯、階差、斜坡、電梯', en: 'Stairs, steps, slopes, elevators', ja: '階段・段差・斜面・エレベーター' },
        items: {
          zh: ['足式 odo 9：樓梯、20 cm 階差、30° 斜坡', '整合 BMS 與門禁系統，自己搭電梯、開門'],
          en: ['Legged odo 9: stairs, 20 cm steps and 30° slopes', 'Elevators and doors through BMS and access-control integration'],
          ja: ['脚式 odo 9：階段、段差 20 cm、勾配 30°', 'BMS・入退室管理との連携でエレベーターとドアを通過']
        }
      },
      {
        title: { zh: '無地圖自主探索', en: 'Autonomous exploration, no map needed', ja: '地図なしの自律探索' },
        items: {
          zh: ['自己選擇下一個探索目標，邊走邊建立地圖', '適用於震後搜救、車輛進不去的巷弄，以及消防人員進入前的濃煙室內'],
          en: ['Picks its next exploration target by itself and maps as it moves', 'For search after an earthquake, lanes no vehicle can enter, and smoke-filled interiors before firefighters go in'],
          ja: ['次の探索目標を自ら選び、移動しながら地図を作成', '地震後の捜索、車両が入れない路地、消防隊員が突入する前の煙が充満した屋内に']
        }
      }
    ]
  },

  platform: {
    title: {
      zh: '透過AI訓練的機器狗',
      en: 'A quadruped trained with AI.',
      ja: 'AI で訓練された四足ロボット'
    },
    sub: {
      zh: '差別在於訓練它的方法，以及讓它真正能工作的大腦。',
      en: 'The difference is how it is trained, and the brain that makes it actually work.',
      ja: '違いは、訓練の方法と、実際に働けるようにする頭脳です。'
    },
    sim: {
      title: { zh: '在模擬中訓練', en: 'Trained in simulation', ja: 'シミュレーションで訓練' },
      bullets: {
        zh: [
          '在 Isaac Sim / Isaac Lab 建立場域的數位孿生',
          '以強化學習訓練運動控制與導航：平地、崎嶇地形、樓梯、斜坡',
          '在模擬環境完成訓練，直接部署到機器人上'
        ],
        en: [
          'Digital twin of your site in Isaac Sim / Isaac Lab',
          'RL locomotion and navigation: flat ground, rough terrain, stairs, slopes',
          'Trained in the simulator, deployed straight to the robot'
        ],
        ja: [
          'Isaac Sim / Isaac Lab 上にあなたの現場のデジタルツインを構築',
          '強化学習による歩行と航法：平地、不整地、階段、斜面',
          'シミュレーターで訓練し、そのままロボットへ展開'
        ]
      },
      alt: {
        zh: 'Nexuni 建立的工地數位孿生，odo 9 在模擬環境中巡檢',
        en: 'Nexuni-built digital twin of a construction site, odo 9 on a simulated inspection round',
        ja: 'Nexuni が構築した工事現場のデジタルツイン。シミュレーション上で巡回点検する odo 9'
      },
    },
    niro: {
      title: { zh: '由 NIRO 驅動', en: 'Powered by NIRO', ja: 'NIRO が駆動' },
      bullets: {
        zh: [
          '一句指令 → 規劃路線 → 執行 → 處理例外狀況',
          '所有 AI 推論都在機器人上的 NVIDIA Jetson 執行，影像不離開場域',
          '提供感知與控制 API，可與既有 CCTV 整合'
        ],
        en: [
          'One instruction → route plan → execution → exception handling',
          '100% local inference on NVIDIA Jetson; footage stays on site',
          'Perception and control API; integrates with your existing CCTV'
        ],
        ja: [
          '指示ひとつ → ルート計画 → 実行 → 例外処理',
          'NVIDIA Jetson 上で 100% ローカル推論。映像は現場から出ない',
          '知覚・制御 API を提供。既存の CCTV とも連携可能'
        ]
      }
    }
  },

  site: {
    title: {
      zh: '快速部署、高擴充性，可整合至現有系統。',
      en: 'Fast to deploy, highly scalable, and integrates with your existing systems.',
      ja: '迅速な導入と高い拡張性。既存システムとも連携できます。'
    },
    cols: [
      {
        title: { zh: '場地與硬體', en: 'Site & hardware', ja: '現場とハードウェア' },
        items: {
          zh: ['路線上設置充電座，機器人會自行返回充電', '充電座旁需要一個 110–220 V 插座', '硬質平坦地面，階差 20 cm 以內（odo 1 為 15 cm）'],
          en: ['Charging dock on the route; the robot returns by itself', 'One 110–220 V outlet at the dock', 'Hard, flat floor; steps up to 20 cm (15 cm on odo 1)'],
          ja: ['ルート上に充電ドックを設置。ロボットは自ら戻って充電', 'ドックに 110–220 V のコンセント 1 口', '硬く平坦な床。段差は 20 cm まで（odo 1 は 15 cm）']
        }
      },
      {
        title: { zh: '軟體與整合', en: 'Software & integration', ja: 'ソフトウェアと連携' },
        items: {
          zh: ['管理介面架設在場域網路，巡邏本身可離線執行', '提供 API 與 Webhook，可串接保全或 MES 平台', '可整合既有 VMS / CCTV'],
          en: ['Dashboard on the site network; the round itself runs offline', 'Open API and webhooks into your security or MES platform', 'Optional VMS / CCTV integration'],
          ja: ['ダッシュボードは現場ネットワーク上。巡回自体はオフラインで動作', 'オープン API と Webhook で警備・MES プラットフォームへ接続', 'VMS / CCTV 連携（任意）']
        }
      },
      {
        title: { zh: '資料與安全', en: 'Data & security', ja: 'データとセキュリティ' },
        items: {
          zh: ['所有 AI 推論都在機器人上執行，不依賴雲端', '影像與報告都留在場域內', '角色權限控管，每一趟巡邏都有完整稽核紀錄'],
          en: ['All inference runs on the robot; no cloud dependency', 'Footage and reports stay inside the site', 'Role-based access and a full audit log of every round'],
          ja: ['AI 推論はすべてロボット上で実行。クラウドに依存しない', '映像とレポートは現場の外に出ない', 'ロールベースのアクセス制御と、全巡回の完全な監査ログ']
        }
      }
    ]
  },

  family: {
    title: {
      zh: '三款型號，依場域需求選擇。',
      en: 'Three models. Pick the one your site needs.',
      ja: '3 つのモデルから、現場のニーズに合わせて選べます。'
    },
    spec: [
      { k: { zh: '型號', en: 'Model', ja: '型番' }, v: 'NXBOT-ODO9' },
      { k: { zh: '尺寸 長 × 寬 × 高', en: 'Dimensions L × W × H', ja: '寸法 L × W × H' }, v: '862 × 595 × 698 mm' },
      { k: { zh: '重量', en: 'Weight', ja: '重量' }, v: '52.9 kg' },
      { k: { zh: '最高步行速度', en: 'Max walking speed', ja: '最高歩行速度' }, v: '1.2 m/s' },
      { k: { zh: '電池續航', en: 'Battery runtime', ja: 'バッテリー駆動時間' }, v: '2.5–3 h' },
      { k: { zh: '防護等級', en: 'Ingress rating', ja: '保護等級' }, v: 'IP67' },
      { k: { zh: '最大跨階高度', en: 'Max step height', ja: '最大段差' }, v: '20 cm' },
      { k: { zh: '最大坡度', en: 'Max slope', ja: '最大勾配' }, v: '30°' },
      { k: { zh: '運算', en: 'Compute', ja: '演算' }, v: 'NVIDIA Jetson Thor' }
    ],
    footnote: {
      zh: '所示規格為 odo 9 設計值，最終量產規格可能有所調整。',
      en: 'Specifications shown are odo 9 design values; final production specifications may vary.',
      ja: '記載の仕様は odo 9 の設計値であり、最終的な量産仕様は変更される場合があります。'
    },
    order: ['odo1', 'odo3', 'odo9'],
    // Rows shown on the two smaller model tiles; the rest lives in the comparison table.
    rows: [
      { id: 'model', label: { zh: '型號', en: 'Model', ja: '型番' } },
      { id: 'env', label: { zh: '環境', en: 'Environment', ja: '環境' } },
      { id: 'ip', label: { zh: '防護等級', en: 'Ingress rating', ja: '保護等級' } },
      { id: 'run', label: { zh: '續航', en: 'Runtime', ja: '駆動時間' } },
      { id: 'step', label: { zh: '跨階 / 坡度', en: 'Step / slope', ja: '段差 / 勾配' } }
    ],
    // Rows in the odo 1 / 3 / 9 comparison table.
    compareRows: [
      { id: 'model', label: { zh: '型號', en: 'Model', ja: '型番' } },
      { id: 'role', label: { zh: '定位', en: 'Role', ja: '位置づけ' } },
      { id: 'loco', label: { zh: '移動方式', en: 'Locomotion', ja: '移動方式' } },
      { id: 'platform', label: { zh: '平台', en: 'Platform', ja: 'プラットフォーム' } },
      { id: 'env', label: { zh: '環境', en: 'Environment', ja: '環境' } },
      { id: 'ip', label: { zh: '防護等級', en: 'Ingress rating', ja: '保護等級' } },
      { id: 'dim', label: { zh: '尺寸', en: 'Dimensions', ja: '寸法' } },
      { id: 'wt', label: { zh: '重量', en: 'Weight', ja: '重量' } },
      { id: 'run', label: { zh: '續航', en: 'Runtime', ja: '駆動時間' } },
      { id: 'step', label: { zh: '跨階 / 坡度', en: 'Step / slope', ja: '段差 / 勾配' } },
      { id: 'compute', label: { zh: '運算', en: 'Compute', ja: '演算' } }
    ],
    models: {
      odo1: {
        id: 'odo1',
        name: 'odo 1',
        logo: 'logo1',
        logoAlt: 'nexuni odo 1',
        logoW: 231,
        role: {
          zh: '入門款：適合室內以及晴天室外的巡邏',
          en: 'Entry model: for indoor patrol and fair-weather outdoor patrol.',
          ja: 'エントリーモデル：屋内と晴天時の屋外の巡回に。'
        },
        alt: {
          zh: 'nexuni odo 1 灰色輪足入門款機器人，搭載黑色 nexuni 感測頭',
          en: 'nexuni odo 1 grey wheeled-leg entry model with a black nexuni sensor head',
          ja: '黒い nexuni センサーヘッドを持つ、グレーの車輪脚エントリーモデル nexuni odo 1'
        },
        vals: {
          model: 'NXBOT-ODO1',
          role: { zh: '入門款', en: 'Entry', ja: 'エントリー' },
          loco: { zh: '輪足', en: 'Wheeled-leg', ja: '車輪脚' },
          platform: { zh: '商用四足平台', en: 'COTS quadruped platform', ja: '市販の四足プラットフォーム' },
          env: { zh: '室內與晴天戶外', en: 'Indoor & fair-weather outdoor', ja: '屋内・晴天時の屋外' },
          ip: 'IP54',
          dim: '70 × 43 × 60 cm',
          wt: '≈ 15 kg',
          run: { zh: '1.5–2 小時 · 自動回充', en: '1.5–2 h · auto-recharge', ja: '1.5–2 時間 · 自動充電' },
          step: '15 cm · 20°',
          compute: 'NVIDIA Jetson Orin NX'
        }
      },
      odo3: {
        id: 'odo3',
        name: 'odo 3',
        logo: 'logo3',
        logoAlt: 'nexuni odo 3',
        logoW: 238,
        role: {
          zh: '進階款：工業級全天候室內外巡檢',
          en: 'Advanced model: industrial-grade all-weather indoor and outdoor inspection.',
          ja: 'アドバンストモデル：産業グレードの全天候屋内外点検。'
        },
        alt: {
          zh: 'nexuni odo 3 銀色輪足四足機器人，搭載感測背包',
          en: 'nexuni odo 3 silver wheeled-leg quadruped with a sensor backpack',
          ja: 'センサーバックパックを搭載した銀色の車輪脚四足ロボット nexuni odo 3'
        },
        vals: {
          model: 'NXBOT-ODO3',
          role: { zh: '進階款', en: 'Advanced', ja: 'アドバンスト' },
          loco: { zh: '輪足', en: 'Wheeled-leg', ja: '車輪脚' },
          platform: { zh: '商用四足平台', en: 'COTS quadruped platform', ja: '市販の四足プラットフォーム' },
          env: { zh: '室內外、全天候', en: 'Indoor & outdoor, all weather', ja: '屋内外、全天候' },
          ip: 'IP56',
          dim: '90 × 44 × 73 cm',
          wt: '≈ 37 kg',
          run: { zh: '2.5–3 小時 · 自動回充', en: '2.5–3 h · auto-recharge', ja: '2.5–3 時間 · 自動充電' },
          step: '20 cm · 30°',
          compute: 'NVIDIA Jetson Thor'
        }
      },
      odo9: {
        id: 'odo9',
        name: 'odo 9',
        logo: 'logo9',
        logoAlt: 'nexuni odo 9',
        logoW: 238,
        role: {
          zh: '旗艦款：工業級室內外巡檢、使用全自製四足平台、IP67 防水防塵規格',
          en: 'Flagship: industrial-grade indoor and outdoor inspection on a fully Nexuni-made quadruped platform, IP67 water and dust protection.',
          ja: 'フラッグシップ：産業グレードの屋内外点検。完全自社製の四足プラットフォーム、IP67 の防水防塵。'
        },
        alt: {
          zh: 'nexuni odo 9 黑色足式旗艦四足機器人，搭載白色感測背包',
          en: 'nexuni odo 9 black legged flagship quadruped with a white sensor backpack',
          ja: '白いセンサーバックパックを搭載した黒い脚式フラッグシップ四足ロボット nexuni odo 9'
        },
        vals: {
          model: 'NXBOT-ODO9',
          role: { zh: '旗艦款', en: 'Flagship', ja: 'フラッグシップ' },
          loco: { zh: '足式', en: 'Legged', ja: '脚式' },
          platform: { zh: 'Nexuni 全自製', en: 'Fully Nexuni-made', ja: 'Nexuni 完全自社製' },
          env: { zh: '室內外、全天候', en: 'Indoor & outdoor, all weather', ja: '屋内外、全天候' },
          ip: 'IP67',
          dim: '86.2 × 59.5 × 69.8 cm',
          wt: '52.9 kg',
          run: { zh: '2.5–3 小時 · 自動回充', en: '2.5–3 h · auto-recharge', ja: '2.5–3 時間 · 自動充電' },
          step: '20 cm · 30°',
          compute: 'NVIDIA Jetson Thor'
        }
      }
    },
    compareCaption: {
      zh: 'odo 1、odo 3 與 odo 9 規格比較',
      en: 'Specification comparison of odo 1, odo 3 and odo 9',
      ja: 'odo 1・odo 3・odo 9 の仕様比較'
    },
    compareSpec: { zh: '規格', en: 'Specification', ja: '仕様' },
    compareHint: { zh: '← 左右滑動比較 →', en: '← SCROLL TO COMPARE →', ja: '← スクロールして比較 →' },
    payloadNote: {
      zh: '標準酬載包含光達、深度攝影機、2 顆攝影機與超音波感測器；PTZ 攝影機、熱像儀、氣體感測器、洩漏偵測等感測酬載，可依專案需求加裝。',
      en: 'The standard payload includes lidar, a depth camera, two cameras and an ultrasonic sensor. Sensing payloads such as a PTZ camera, thermal camera, gas sensor or leak detector can be added case by case.',
      ja: '標準ペイロードは LiDAR、深度カメラ、カメラ 2 台、超音波センサー。PTZ カメラ、サーマルカメラ、ガスセンサー、漏えい検知などのセンシングペイロードは、案件ごとに追加できます。'
    }
  },

  cta: {
    title: {
      zh: '想知道它在你的場域怎麼運作？',
      en: 'See how it runs on your site.',
      ja: 'あなたの現場でどう動くか、ご覧ください。'
    },
    sub: {
      zh: '告訴我們場域條件，我們一起規劃部署方式。',
      en: 'Tell us about the site and we will scope the deployment with you.',
      ja: '現場の条件をお聞かせください。導入方法を一緒に検討します。'
    },
    primary: { zh: '預約現場評估', en: 'Book a site assessment', ja: '現場アセスメントを予約' },
  }
};

/** <picture> for an image that ships as webp + png (renders) or webp + jpg (photos). */
function renderPic(a, alt, className, eager) {
  return (
    <picture>
      <source srcSet={a.webp} type="image/webp" />
      <img
        className={className}
        src={a.png || a.jpg}
        alt={alt}
        width={a.w}
        height={a.h}
        loading={eager ? 'eager' : 'lazy'}
        decoding="async"
      />
    </picture>
  );
}

/** Spec values: plain strings are numeric data (mono); localized objects are text. */
function valClass(v) {
  return typeof v === 'string' ? undefined : 'odo-val--text';
}

class OdoPage extends Component {
  constructor(props) {
    super(props);
    this.rootRef = React.createRef();
    this.disposeRise = null;
    this.videoIO = null;
    this.disposeCompare = null;
    const canQuery = typeof window !== 'undefined' && typeof window.matchMedia === 'function';
    this.state = {
      // With reduced motion the clips get native controls and load nothing until asked.
      reduceMotion: canQuery ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false
    };
    this.update_lang = this.update_lang.bind(this);
    this.navigate = this.navigate.bind(this);
    this.onPlayClick = this.onPlayClick.bind(this);
    this.hidePlayButton = this.hidePlayButton.bind(this);
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    updateProductSEO('odo', this.lang(), '/tech/odo');
    this.disposeRise = observeRise(this.rootRef.current);
    this.observeVideos();
    this.watchCompareScroll();
  }

  componentWillUnmount() {
    if (this.disposeRise) {
      this.disposeRise();
      this.disposeRise = null;
    }
    if (this.videoIO) {
      this.videoIO.disconnect();
      this.videoIO = null;
    }
    if (this.disposeCompare) {
      this.disposeCompare();
      this.disposeCompare = null;
    }
  }

  /**
   * One IntersectionObserver for the deployment clips: a clip plays while at
   * least a quarter of it is on screen and pauses otherwise. A refused play()
   * (autoplay policy, data saver) reveals the PLAY button next to the clip;
   * an AbortError only means a pause() overtook the play() and is ignored.
   */
  observeVideos() {
    const root = this.rootRef.current;
    if (!root || this.state.reduceMotion) return;
    const clips = Array.from(root.querySelectorAll('video.odo-video'));
    if (clips.length === 0) return;
    const tryPlay = (v) => {
      const p = v.play();
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          if (err && err.name === 'AbortError') return;
          const btn = v.nextElementSibling;
          if (btn && btn.classList.contains('odo-video__play')) btn.classList.add('is-shown');
        });
      }
    };
    if (!('IntersectionObserver' in window)) {
      clips.forEach(tryPlay);
      return;
    }
    this.videoIO = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        const v = en.target;
        if (en.isIntersecting && en.intersectionRatio >= 0.25) {
          if (v.paused) tryPlay(v);
        } else {
          // Off screen. Clear the autoplay flag as well: a clip that has not
          // buffered yet is still "paused", and would otherwise start on its
          // own once it has enough data.
          v.autoplay = false;
          v.pause();
        }
      });
    }, { threshold: [0, 0.25] });
    clips.forEach((v) => this.videoIO.observe(v));
  }

  /** PLAY fallback: a click is a user gesture, so play() is allowed. */
  onPlayClick(e) {
    const v = e.currentTarget.previousElementSibling;
    if (!v || typeof v.play !== 'function') return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  /** Once a clip is actually playing its PLAY button goes away. */
  hidePlayButton(e) {
    const btn = e.currentTarget.nextElementSibling;
    if (btn && btn.classList.contains('odo-video__play')) btn.classList.remove('is-shown');
  }

  /** Marks the comparison table's scroll box while it can still scroll right (the CSS fades that edge). */
  watchCompareScroll() {
    const root = this.rootRef.current;
    const box = root && root.querySelector('.odo-compare__scroll');
    if (!box) return;
    const update = () => {
      box.classList.toggle('is-overflowing', box.scrollLeft + box.clientWidth < box.scrollWidth - 1);
    };
    box.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update, () => {});
    update();
    this.disposeCompare = () => {
      box.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }

  lang() {
    return LANGS.includes(this.props.lang) ? this.props.lang : 'en';
  }

  update_lang(newLang) {
    if (typeof this.props.updateLang === 'function') this.props.updateLang(newLang);
  }

  navigate(route) {
    window.location.href = buildPath(this.lang(), route);
  }

  // Real <a href>s stay real: modified clicks open the link in a new tab.
  go(e, route, eventName, params) {
    if (eventName) trackEvent(eventName, params);
    if (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button && e.button !== 0)) return;
      e.preventDefault();
    }
    this.navigate(route);
  }

  /**
   * Deployment footage. By default a muted, looping autoplay clip that the
   * observer in observeVideos() runs only while it is on screen, followed by
   * its (hidden) PLAY fallback. Under reduced motion: native controls,
   * preload="none", no autoplay, no loop. `label` is the clip's a11y text.
   */
  renderVideo(base, poster, w, h, label) {
    const sources = [
      <source key="webm" src={base + '.webm'} type="video/webm" />,
      <source key="mp4" src={base + '.mp4'} type="video/mp4" />
    ];
    if (this.state.reduceMotion) {
      return (
        <video
          className="odo-video"
          controls
          muted
          playsInline
          preload="none"
          poster={poster}
          width={w}
          height={h}
          aria-label={label}
          disablePictureInPicture
          disableRemotePlayback
        >
          {sources}
        </video>
      );
    }
    return (
      <>
        <video
          className="odo-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
          width={w}
          height={h}
          aria-label={label}
          disablePictureInPicture
          disableRemotePlayback
          onPlaying={this.hidePlayButton}
        >
          {sources}
        </video>
        <button type="button" className="odo-video__play" onClick={this.onPlayClick}>PLAY</button>
      </>
    );
  }

  /** Section head: the headline stands alone, plus at most one sub sentence. */
  renderHead(t, s, id) {
    return (
      <div className="odo-head bleed rise">
        <h2 className="sec-title" id={id}>{t(s.title)}</h2>
        {s.sub && <p className="sec-lede">{t(s.sub)}</p>}
      </div>
    );
  }

  /* ---------------- 1. Hero ---------------- */
  /** Real photo with webp/jpg sources and a smaller mobile crop. */
  renderPhoto(asset, alt, className, eager) {
    return (
      <picture className={className}>
        {asset.m && <source media="(max-width: 780px)" srcSet={asset.m} type="image/webp" />}
        <source srcSet={asset.webp} type="image/webp" />
        <img src={asset.jpg} alt={alt} width={asset.w} height={asset.h} loading={eager ? 'eager' : 'lazy'} decoding="async" fetchPriority={eager ? 'high' : undefined} />
      </picture>
    );
  }


  /* ---------------- 1. Hero: full-bleed odo 9 photo, black gradient, the series lockup and one description ---------------- */
  renderHero(lang, t, tl) {
    const h = COPY.hero;
    return (
      <section className="odo-hero" aria-labelledby="odo-hero-title">
        <div className="odo-hero__bg" aria-hidden="true">
          {this.renderPhoto(ASSETS.heroSub, '', 'odo-hero__photo', true)}
          <span className="odo-hero__scrim"></span>
        </div>
        <div className="odo-hero__copy bleed">
          <div className="odo-hero__kicker odo-in" style={{ '--i': 0 }}>
            <img className="odo-hero__lockup" src={ASSETS.logoOdo} alt="nexuni odo" height="22" decoding="async" />
            <span className="odo-hero__series">{t(h.seriesLine)}</span>
          </div>
          <h1 id="odo-hero-title" className="h-display odo-hero__name odo-in" style={{ '--i': 1 }}>{t(COPY.name)}</h1>
          <p className="p-lede odo-hero__desc odo-in" style={{ '--i': 2 }}>{t(h.desc)}</p>
        </div>
      </section>
    );
  }

  /* ---------------- 1b. At a glance: industry challenges | our product (same band as the other product pages) ---------------- */
  renderGlance(lang, t, tl) {
    const g = COPY.glance;
    return (
      <section className="odo-glance" aria-label={t(g.titleCus) + ' / ' + t(g.titlePs)}>
        <div className="odo-glance__half odo-glance__half--cus rise">
          <h2 className="odo-glance__title">{t(g.titleCus)}</h2>
          <ul className="odo-list odo-glance__list">
            {tl(g.cus).map((line, i) => <li key={i}>{line}</li>)}
          </ul>
        </div>
        <div className="odo-glance__half odo-glance__half--ps rise">
          <h2 className="odo-glance__title">{t(g.titlePs)}</h2>
          <div className="odo-glance__body">
            {tl(g.ps).map((para, i) => <p key={i}>{para}</p>)}
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- Brand note: two sentences, three lockups ---------------- */
  renderBrand(lang, t) {
    const b = COPY.brand;
    return (
      <section className="odo-brand odo-g900 bleed" aria-labelledby="odo-brand-title">
        <div className="odo-brand__inner rise">
          <div className="odo-brand__marks" aria-hidden="true">
            <img src={ASSETS.logoOdo} alt="" height="28" decoding="async" />
          </div>
          <div className="odo-brand__text">
            <h2 id="odo-brand-title" className="odo-brand__title">{t(b.title)}</h2>
            <p className="odo-brand__p">{t(b.text)}</p>
          </div>
        </div>
      </section>
    );
  }

  /* ---------------- 2. What it does ---------------- */
  renderWhat(lang, t) {
    const s = COPY.what;
    return (
      <section className="odo-sec odo-g900 odo-what odo-what--bare" aria-label={t(s.label)}>
        <div className="odo-cols odo-cols--3">
          {s.cards.map((c, i) => (
            <div className="odo-col rise" key={c.title.en} style={{ transitionDelay: (i * 60) + 'ms' }}>
              <span className="odo-icon">{ICON[c.icon]}</span>
              <h3 className="odo-h3">{t(c.title)}</h3>
              <p className="odo-p">{t(c.text)}</p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  /* ---------------- 3. Capabilities: patrol, console, evidence ---------------- */
  renderCapabilities(lang, t, tl) {
    const s = COPY.capabilities;
    return (
      <section className="odo-sec odo-g850 odo-capabilities" aria-labelledby="odo-capabilities-title">
        {this.renderHead(t, s, 'odo-capabilities-title')}
        {this.renderPatrolRow(t, tl, s.patrol)}
        {this.renderConsoleRow(lang, t, s.console)}
        {this.renderEvidenceRow(t, tl, s.evidence)}
      </section>
    );
  }

  /** Row A: field footage left, copy right. Nothing overlays the footage. */
  renderPatrolRow(t, tl, p) {
    return (
      <div className="odo-tiles odo-patrol rise">
        <figure className="odo-patrol__media">
          <div className="odo-patrol__frame">
            {this.renderVideo(ASSETS.fieldVideo, ASSETS.fieldPoster, 540, 940, t(p.alt))}
          </div>
        </figure>
        <div className="odo-copy odo-patrol__copy">
          <h3 className="odo-h3 odo-h3--lg">{t(p.title)}</h3>
          <p className="odo-p">{t(p.sub)}</p>
          <ul className="odo-list">
            {tl(p.bullets).map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>
      </div>
    );
  }

  /** Row B: head (title | sub), full-width console footage, three feature cells, foot. */
  renderConsoleRow(lang, t, c) {
    return (
      <div className="odo-tiles odo-console rise">
        <div className="odo-console__head">
          <h3 className="odo-h3 odo-h3--lg">{t(c.title)}</h3>
          <p className="odo-p">{t(c.sub)}</p>
        </div>

        <figure className="odo-console__media">
          <div className="odo-media__frame odo-media__frame--wide">
            {this.renderVideo(ASSETS.consoleVideo, ASSETS.consolePoster, 1600, 596, t(c.alt))}
          </div>
        </figure>

        <ul className="odo-stats" role="list">
          {c.features.map((f) => (
            <li className="odo-stat" key={f.title.en}>
              <span className="odo-stat__title">{t(f.title)}</span>
              <span className="odo-stat__label">{t(f.text)}</span>
            </li>
          ))}
        </ul>

      </div>
    );
  }

  /** Row C: copy left, the checkpoint record schema right (field definitions, not a sample). */
  renderEvidenceRow(t, tl, ev) {
    const r = ev.record;
    return (
      <div className="odo-tiles odo-evidence rise">
        <div className="odo-copy odo-evidence__copy">
          <h3 className="odo-h3 odo-h3--lg">{t(ev.title)}</h3>
          <p className="odo-p">{t(ev.sub)}</p>
          <ul className="odo-list">
            {tl(ev.bullets).map((b) => <li key={b}>{b}</li>)}
          </ul>
        </div>

        <figure className="odo-evidence__panel">
          <div className="odo-record">
            <div className="odo-doc__bar">
              <span className="odo-doc__dots" aria-hidden="true"><i></i><i></i><i></i></span>
              <span className="odo-cap">{t(r.label)}</span>
            </div>
            <dl className="odo-rec__rows">
              {r.fields.map((f) => (
                <div className="odo-rec__row" key={f.k}>
                  <dt>{f.k}</dt>
                  {f.chips ? (
                    <dd className="odo-rec__chips">
                      {f.chips.map((chip, i) => (
                        <span className={'odo-rec__pass' + (i ? ' odo-rec__pass--dim' : '')} key={chip}>{chip}</span>
                      ))}
                      <span className="odo-rec__note">{t(f.v)}</span>
                    </dd>
                  ) : (
                    <dd>{t(f.v)}</dd>
                  )}
                </div>
              ))}
            </dl>
          </div>
        </figure>
      </div>
    );
  }

  /* ---------------- 4. Terrain: where it goes ---------------- */
  renderTerrain(lang, t, tl) {
    const s = COPY.terrain;
    return (
      <section className="odo-sec odo-g900 odo-terrain" aria-labelledby="odo-terrain-title">
        {this.renderHead(t, s, 'odo-terrain-title')}

        {/* Photo 7fr | two stacked cards 5fr. */}
        <div className="odo-tiles odo-feature rise">
          <figure className="odo-feature__media">
            <div className="odo-media__frame odo-feature__frame">
              {renderPic(ASSETS.terrain, t(s.alt), 'odo-feature__img')}
            </div>
          </figure>
          <div className="odo-feature__body">
            {s.cards.map((c) => (
              <div className="odo-feature__card" key={c.title.en}>
                <h3 className="odo-h3">{t(c.title)}</h3>
                <ul className="odo-list">
                  {tl(c.items).map((it) => <li key={it}>{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  /** Small NIRO core glyph (hexagonal node mesh), decorative. Colours come from the tokens. */
  renderNiroGlyph() {
    const nodes = [[60, 34], [82.5, 47], [82.5, 73], [60, 86], [37.5, 73], [37.5, 47]];
    return (
      <div className="odo-niro" aria-hidden="true">
        <svg className="odo-niro__glyph" viewBox="0 0 120 120" focusable="false">
          <circle cx="60" cy="60" r="57" fill="none" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1" />
          <circle className="odo-niro__disc" cx="60" cy="60" r="46" stroke="currentColor" strokeOpacity="0.55" strokeWidth="1.2" />
          <g stroke="currentColor" strokeOpacity="0.45" strokeWidth="1" fill="none">
            {nodes.map((n, i) => {
              const m = nodes[(i + 1) % nodes.length];
              return <line key={'p' + i} x1={n[0]} y1={n[1]} x2={m[0]} y2={m[1]} />;
            })}
            {nodes.map((n, i) => (
              <line key={'s' + i} x1={n[0]} y1={n[1]} x2="60" y2="60" />
            ))}
          </g>
          <g fill="currentColor">
            {nodes.map((n, i) => (
              <circle key={i} cx={n[0]} cy={n[1]} r="2.4" />
            ))}
            <circle cx="60" cy="60" r="3.8" />
          </g>
        </svg>
        <span className="odo-niro__label">NIRO</span>
      </div>
    );
  }

  /* ---------------- 5. The platform ---------------- */
  renderPlatform(lang, t, tl) {
    const s = COPY.platform;
    return (
      <section className="odo-sec odo-g850 odo-platform" aria-labelledby="odo-platform-title">
        {this.renderHead(t, s, 'odo-platform-title')}

        <div className="odo-cols odo-cols--2">
          <div className="odo-col odo-pcol rise">
            <h3 className="odo-h3">{t(s.sim.title)}</h3>
            <ul className="odo-list">
              {tl(s.sim.bullets).map((b) => <li key={b}>{b}</li>)}
            </ul>
            {/* Half-bleed: the twin photo runs to the left edge of the viewport. */}
            <figure className="odo-pcol__media odo-pcol__media--left">
              <div className="odo-media__frame">
                {this.renderVideo(ASSETS.simVideo, ASSETS.simPoster, 1280, 720, t(s.sim.alt))}
              </div>
            </figure>
          </div>

          <div className="odo-col odo-pcol rise" style={{ transitionDelay: '60ms' }}>
            <h3 className="odo-h3">{t(s.niro.title)}</h3>
            <ul className="odo-list">
              {tl(s.niro.bullets).map((b) => <li key={b}>{b}</li>)}
            </ul>
            {/* Half-bleed: the NIRO panel runs to the right edge of the viewport. */}
            <figure className="odo-pcol__media odo-pcol__media--right">
              <div className="odo-niro-panel">
                {this.renderNiroGlyph()}
              </div>
            </figure>
          </div>
        </div>

      </section>
    );
  }

  /* ---------------- 6. What a site needs ---------------- */
  renderSite(lang, t, tl) {
    const s = COPY.site;
    return (
      <section className="odo-sec odo-g900 odo-site" aria-labelledby="odo-site-title">
        {this.renderHead(t, s, 'odo-site-title')}

        <div className="odo-cols odo-cols--3">
          {s.cols.map((c, i) => (
            <div className="odo-col rise" key={c.title.en} style={{ transitionDelay: (i * 60) + 'ms' }}>
              <h3 className="odo-h3">{t(c.title)}</h3>
              <ul className="odo-list">
                {tl(c.items).map((it) => <li key={it}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>

      </section>
    );
  }

  /** Key spec rows on an odo 3 / odo 1 tile. */
  renderModelRows(t, rows, m) {
    return (
      <dl className="odo-model__rows">
        {rows.map((r) => {
          const v = m.vals[r.id];
          return (
            <div className="odo-model__row" key={r.id}>
              <dt>{t(r.label)}</dt>
              <dd className={valClass(v)}>{t(v)}</dd>
            </div>
          );
        })}
      </dl>
    );
  }

  /* ---------------- 7. odo series: the family ---------------- */
  renderFamily(lang, t) {
    const s = COPY.family;
    const m9 = s.models.odo9;
    const half = Math.ceil(s.spec.length / 2);

    return (
      <section className="odo-sec odo-g850 odo-family" aria-labelledby="odo-family-title">
        {this.renderHead(t, s, 'odo-family-title')}

        {/* Featured: odo 9. Render on a black ground left, specs right. */}
        <article className="odo-tiles odo-flag rise" aria-labelledby="odo-flag-title">
          <div className="odo-flag__visual">
            <div className="odo-flag__render">
              <span className="odo-flag__spot" aria-hidden="true"></span>
              {renderPic(ASSETS.odo9, t(m9.alt), 'odo-flag__img')}
            </div>
          </div>

          <div className="odo-flag__info">
              <div className="odo-model__head">
                <img className="odo-model__logo" src={ASSETS[m9.logo]} alt={m9.logoAlt} width={m9.logoW} height="34" decoding="async" />
              </div>
              <p className="odo-model__role odo-flag__role">{t(m9.role)}</p>
              <div className="odo-spec-cols odo-flag__speckey">
                {[s.spec.slice(0, Math.ceil(s.spec.length / 2)), s.spec.slice(Math.ceil(s.spec.length / 2))].map((col, ci) => (
                  <table className="odo-spec" key={ci}>
                    <tbody>
                      {col.map((r) => (
                        <tr key={r.k.en}>
                          <th scope="row">{t(r.k)}</th>
                          <td className={valClass(r.v)}>{t(r.v)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </div>
              <p className="odo-footnote">{t(s.footnote)}</p>
          </div>
        </article>

        {/* odo 3 and odo 1: two half-width tiles. */}
        <div className="odo-tiles odo-models rise">
          {[s.models.odo3, s.models.odo1].map((m) => (
            <article className="odo-model" key={m.id} aria-labelledby={'odo-' + m.id + '-title'}>
              <div className={'odo-model__render odo-model__render--' + m.id}>
                {renderPic(ASSETS[m.id], t(m.alt), 'odo-model__img')}
              </div>
              <div className="odo-model__body">
                <img className="odo-model__logo" src={ASSETS[m.logo]} alt={m.logoAlt} width={m.logoW} height="34" loading="lazy" decoding="async" />
                <h3 id={'odo-' + m.id + '-title'} className="odo-model__role">{t(m.role)}</h3>
                {this.renderModelRows(t, s.rows, m)}
              </div>
            </article>
          ))}
        </div>

        {/* Comparison table: full width inside the bleed padding, scrolls on narrow screens. */}
        <div className="odo-compare bleed rise">
          <p className="odo-cap odo-compare__hint" aria-hidden="true">{t(s.compareHint)}</p>
          <div className="odo-compare__scroll">
            <table className="odo-compare__table">
              <caption className="odo-visually-hidden">{t(s.compareCaption)}</caption>
              <thead>
                <tr>
                  <th scope="col"><span className="odo-visually-hidden">{t(s.compareSpec)}</span></th>
                  {s.order.map((id) => (
                    <th scope="col" key={id}>{s.models[id].name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {s.compareRows.map((r) => (
                  <tr key={r.id}>
                    <th scope="row">{t(r.label)}</th>
                    {s.order.map((id) => {
                      const v = s.models[id].vals[r.id];
                      return <td key={id} className={valClass(v)}>{t(v)}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="odo-foot odo-compare__note">{t(s.payloadNote)}</p>
        </div>
      </section>
    );
  }

  /* ---------------- 8. CTA band ---------------- */
  renderCta(lang, t) {
    const s = COPY.cta;
    return (
      <section className="odo-close bleed" aria-labelledby="odo-cta-title">
        <div className="odo-close__copy rise">
          <h2 id="odo-cta-title">{t(s.title)}</h2>
          <p>{t(s.sub)}</p>
        </div>
        <div className="odo-close__action rise">
            <a
              className="btn-primary"
              href={buildPath(lang, CONTACT_ROUTE)}
              onClick={(e) => this.go(e, CONTACT_ROUTE, 'select_cta', { location: 'odo_page', cta: 'site_assessment' })}
            >
              {t(s.primary)}<span className="arrow" aria-hidden="true">→</span>
            </a>
        </div>
      </section>
    );
  }

  render() {
    const lang = this.lang();
    // t() accepts a { zh, en, ja } object or a plain string (numeric spec data).
    const t = (o) => (typeof o === 'string' ? o : (o && (o[lang] || o.en)) || '');
    const tl = (o) => (o && (o[lang] || o.en)) || [];

    return (
      <div id="body" className={lang + ' odo_page'} ref={this.rootRef}>
        <AnnouncementBanner lang={lang} />
        <Navbar lang={lang} updateLangFunc={this.update_lang} />
        <Breadcrumb
          lang={lang}
          items={[
            { label: COPY.crumbProducts, route: PRODUCTS_ROUTE },
            { label: COPY.name }
          ]}
        />

        <div className="odo-main">
          {this.renderHero(lang, t, tl)}
          {this.renderGlance(lang, t, tl)}
          {this.renderWhat(lang, t)}
          {this.renderCapabilities(lang, t, tl)}
          {this.renderTerrain(lang, t, tl)}
          {this.renderPlatform(lang, t, tl)}
          {this.renderSite(lang, t, tl)}
          {this.renderBrand(lang, t)}
          {this.renderFamily(lang, t)}
          {this.renderCta(lang, t)}
        </div>

        <Footer lang={lang} navigate={this.navigate} />
      </div>
    );
  }
}

export default OdoPage;
